import React, { useEffect, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import QRCode from 'react-qr-code';
import useAuth from '@contexts/useAuth';
import useSnackbar from '@hooks/useSnackbar';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import PageHeader, {
  PAGE_HEADER_VARIANT_SMALL,
} from '@components/2-molecules/PageHeader';
import SettingSwitch from '@components/2-molecules/SettingSwitch';
import Button, {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_OUTLINE,
  BUTTON_VARIANT_TEXT,
} from '@components/1-atoms/Button';
import { getMfaStatus, startMfaSetup, verifyMfaSetup, disableMfa } from '@api/auth';
import type { MfaStatusResponse } from '@api/types/auth';
import { Frame } from '../Settings.styled';
import {
  Wrapper,
  SetupSection,
  StepCard,
  StepNumber,
  StepHeader,
  StepTitle,
  StepDescription,
  QrContainer,
  SecretCode,
  SecretText,
  VerifyForm,
  CodeInput,
  ActionRow,
  EnforcementNote,
} from './SecuritySettings.styled';

type SetupStep = 'idle' | 'loading' | 'qr' | 'verifying';

const SecuritySettings: React.FC = () => {
  const intl = useIntl();
  const { user, updateMfaEnabled } = useAuth();
  const { showSuccess, showError } = useSnackbar();

  const [mfaStatus, setMfaStatus] = useState<MfaStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupStep, setSetupStep] = useState<SetupStep>('idle');
  const [setupData, setSetupData] = useState<{ secretCode: string; qrUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  useEffect(() => {
    if (!user?.token) return;
    getMfaStatus(user.token)
      .then((status) => setMfaStatus(status))
      .catch(() => showError(intl.formatMessage({
        id: 'settings.security.loadError',
        defaultMessage: 'Failed to load MFA status',
      })))
      .finally(() => setLoading(false));
  }, [user?.token, showError, intl]);

  const handleStartSetup = useCallback(async () => {
    if (!user?.token) return;
    setSetupStep('loading');
    try {
      const result = await startMfaSetup(user.token);
      setSetupData({ secretCode: result.secretCode, qrUri: result.qrUri });
      setSetupStep('qr');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start MFA setup';
      showError(msg);
      setSetupStep('idle');
      setSetupData(null);
    }
  }, [user?.token, showError]);

  const handleVerifySetup = useCallback(async () => {
    if (!user?.token || !code.trim()) return;
    setSetupStep('verifying');
    try {
      await verifyMfaSetup(user.token, code.trim());
      setMfaStatus({ enabled: true, type: 'COGNITO' });
      updateMfaEnabled(true);
      setSetupStep('idle');
      setSetupData(null);
      setCode('');
      showSuccess(intl.formatMessage({
        id: 'settings.security.mfaEnabled',
        defaultMessage: 'Two-factor authentication enabled',
      }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to verify MFA code';
      showError(msg);
      setSetupStep('qr');
      setCode('');
    }
  }, [user?.token, code, updateMfaEnabled, showError, showSuccess, intl]);

  const handleDisable = useCallback(async () => {
    if (!user?.token) return;
    setShowDisableConfirm(false);
    setDisabling(true);
    try {
      await disableMfa(user.token);
      setMfaStatus({ enabled: false, type: 'NONE' });
      updateMfaEnabled(false);
      showSuccess(intl.formatMessage({
        id: 'settings.security.mfaDisabled',
        defaultMessage: 'Two-factor authentication disabled',
      }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to disable MFA';
      showError(msg);
    } finally {
      setDisabling(false);
    }
  }, [user?.token, updateMfaEnabled, showError, showSuccess, intl]);

  const handleCopySecret = useCallback(async (secret: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: no-op
    }
  }, []);

  const handleCancelSetup = useCallback(() => {
    setSetupStep('idle');
    setSetupData(null);
    setCode('');
  }, []);

  const handleCodeKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && code.trim().length === 6) {
        handleVerifySetup();
      }
    },
    [code, handleVerifySetup],
  );

  const isEnabled = mfaStatus?.enabled ?? false;
  const isSetupInProgress = setupStep !== 'idle';
  const isOrgEnforced = user?.mfaEnforced ?? false;

  const handleMfaToggle = useCallback(() => {
    if (isEnabled) {
      if (!isOrgEnforced) setShowDisableConfirm(true);
    } else {
      handleStartSetup();
    }
  }, [isEnabled, isOrgEnforced, handleStartSetup]);

  if (loading) {
    return (
      <Wrapper>
        <PageHeader
          variant={PAGE_HEADER_VARIANT_SMALL}
          title={intl.formatMessage({
            id: 'settings.security.title',
            defaultMessage: 'Two-Factor Authentication',
          })}
        />
        <Frame>
          <StepDescription>
            {intl.formatMessage({
              id: 'settings.security.loading',
              defaultMessage: 'Loading security settings...',
            })}
          </StepDescription>
        </Frame>
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid="security-settings">
      <ConfirmDialog
        show={showDisableConfirm}
        title={intl.formatMessage({
          id: 'settings.security.disableTitle',
          defaultMessage: 'Disable Two-Factor Authentication',
        })}
        confirmLabel={intl.formatMessage({
          id: 'settings.security.disableConfirm',
          defaultMessage: 'Disable 2FA',
        })}
        onConfirm={handleDisable}
        onCancel={() => setShowDisableConfirm(false)}
      >
        <p>
          {intl.formatMessage({
            id: 'settings.security.disableWarning',
            defaultMessage: 'Are you sure you want to disable two-factor authentication? This will make your account less secure.',
          })}
        </p>
      </ConfirmDialog>

      <PageHeader
        dataTestId="settings-page__security-header"
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage({
          id: 'settings.security.title',
          defaultMessage: 'Two-Factor Authentication',
        })}
      />

      <Frame>
        <SettingSwitch
          dataTestId="settings-page__security__mfa"
          label={intl.formatMessage({
            id: 'settings.security.mfaLabel',
            defaultMessage: 'Two-factor authentication',
          })}
          supportingText={intl.formatMessage({
            id: 'settings.security.mfaDescription',
            defaultMessage: 'Add an extra layer of security by requiring a verification code from your authenticator app when signing in.',
          })}
          switchProps={{
            disabled: (isOrgEnforced && isEnabled) || disabling || isSetupInProgress,
            enabled: isEnabled,
            onChange: handleMfaToggle,
          }}
        />

        {isEnabled && isOrgEnforced && (
          <EnforcementNote>
            {intl.formatMessage({
              id: 'settings.security.orgEnforced',
              defaultMessage: 'Your organization requires 2FA. Contact your admin to change this policy.',
            })}
          </EnforcementNote>
        )}
      </Frame>

      {isSetupInProgress && (
        <SetupSection>
          {setupStep === 'loading' && (
            <Frame>
              <StepDescription>
                {intl.formatMessage({
                  id: 'settings.security.generating',
                  defaultMessage: 'Generating your authenticator secret...',
                })}
              </StepDescription>
            </Frame>
          )}

          {(setupStep === 'qr' || setupStep === 'verifying') && setupData && (
            <>
              <StepCard>
                <StepHeader>
                  <StepNumber>1</StepNumber>
                  <StepTitle>
                    {intl.formatMessage({
                      id: 'settings.security.step1',
                      defaultMessage: 'Scan QR Code',
                    })}
                  </StepTitle>
                </StepHeader>

                <StepDescription>
                  {intl.formatMessage({
                    id: 'settings.security.step1Description',
                    defaultMessage: 'Open your authenticator app (Google Authenticator, Authy, 1Password, etc.) and scan the QR code below.',
                  })}
                </StepDescription>

                <QrContainer>
                  <QRCode value={setupData.qrUri} size={180} level="M" />
                </QrContainer>

                <StepDescription>
                  {intl.formatMessage({
                    id: 'settings.security.manualEntry',
                    defaultMessage: "Or enter this secret key manually:",
                  })}
                </StepDescription>

                <SecretCode>
                  <SecretText>{setupData.secretCode}</SecretText>
                  <Button
                    variant={BUTTON_VARIANT_TEXT}
                    onClick={() => handleCopySecret(setupData.secretCode)}
                    stretch={false}
                  >
                    {copied
                      ? intl.formatMessage({ id: 'settings.security.copied', defaultMessage: 'Copied!' })
                      : intl.formatMessage({ id: 'settings.security.copy', defaultMessage: 'Copy' })}
                  </Button>
                </SecretCode>
              </StepCard>

              <StepCard>
                <StepHeader>
                  <StepNumber>2</StepNumber>
                  <StepTitle>
                    {intl.formatMessage({
                      id: 'settings.security.step2',
                      defaultMessage: 'Enter Verification Code',
                    })}
                  </StepTitle>
                </StepHeader>

                <StepDescription>
                  {intl.formatMessage({
                    id: 'settings.security.step2Description',
                    defaultMessage: 'Enter the 6-digit code shown in your authenticator app.',
                  })}
                </StepDescription>

                <VerifyForm>
                  <CodeInput
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={handleCodeKeyDown}
                    disabled={setupStep === 'verifying'}
                    autoFocus
                  />
                  <Button
                    variant={BUTTON_VARIANT_PRIMARY}
                    onClick={handleVerifySetup}
                    disabled={code.trim().length !== 6 || setupStep === 'verifying'}
                    stretch={false}
                  >
                    {setupStep === 'verifying'
                      ? intl.formatMessage({ id: 'settings.security.verifying', defaultMessage: 'Verifying...' })
                      : intl.formatMessage({ id: 'settings.security.verifyEnable', defaultMessage: 'Verify & Enable' })}
                  </Button>
                </VerifyForm>
              </StepCard>

              <ActionRow>
                <Button
                  variant={BUTTON_VARIANT_OUTLINE}
                  onClick={handleCancelSetup}
                  disabled={setupStep === 'verifying'}
                  stretch={false}
                >
                  {intl.formatMessage({ id: 'settings.security.cancel', defaultMessage: 'Cancel' })}
                </Button>
              </ActionRow>
            </>
          )}
        </SetupSection>
      )}
    </Wrapper>
  );
};

export default SecuritySettings;
